/**
 * Struct definitions for SequencerState and related types
 * These mirror the AssemblyScript structures in as/assembly/sequencer.ts
 *
 * Note: AssemblyScript class instances have fields laid out sequentially in memory.
 * Pointers (usize) are 4 bytes in WASM.
 */

import { Struct } from './struct.ts'

export const MAX_NOTES = 128
export const MAX_PITCHES = 16

// NoteData: pitches (Float32Array pointer), duration (f32), velocity (f32), hold (f32), glide (f32)
export const NoteDataStruct = Struct({
  pitches: 'u32', // Pointer to Float32Array (usize in WASM = 4 bytes, offset 0)
  duration: 'f32', // offset 4
  velocity: 'f32', // offset 8
  hold: 'f32', // offset 12
  glide: 'f32', // offset 16
})

// SequenceData: id (i32), notes (NoteDataArray pointer), swing (f32), offset (f32), exponent (f32)
export const SequenceDataStruct = Struct({
  id: 'i32',
  notes: 'u32', // Pointer to NoteDataArray (usize in WASM = 4 bytes)
  swing: 'f32',
  offset: 'f32',
  exponent: 'f32',
})

// SequencerState - all fields in order as defined in sequencer.ts
// Field offsets calculated based on AssemblyScript class layout:
// - Pointers (usize) = 4 bytes
// - i32 = 4 bytes
// - f32 = 4 bytes
export const SequencerStateStruct = Struct({
  data: 'u32', // Pointer to SequenceData (offset 0)
  maxPitches: 'i32', // offset 4
  noteStarts: 'u32', // Pointer to StaticArray<i32> (offset 8)
  totalSamples: 'i32', // offset 12
  currentIndex: 'i32', // offset 16
  prevIndex: 'i32', // offset 20
  lastTrig: 'f32', // offset 24
  lastResetTrig: 'f32', // offset 28
  arrayOffset: 'i32', // offset 32
  slideTime: 'f32', // offset 36
  slideDuration: 'f32', // offset 40
  trigHoldSamplesRemaining: 'i32', // offset 44
  pendingTrigHold: 'i32', // offset 48
  lastNoteIndex: 'i32', // offset 52
  shuffleTriggerCount: 'i32', // offset 56
  shuffledIndices: 'u32', // Pointer to StaticArray<i32> | null (offset 60, 0 = null)
  shuffleCurrentIndex: 'i32', // offset 64
  lastShuffleTrig: 'f32', // offset 68
  lastShuffleSampleCounter: 'i32', // offset 72
  lastShuffleTriggerSample: 'i32', // offset 76
  rotationTriggerCount: 'i32', // offset 80
  rotatedIndices: 'u32', // Pointer to StaticArray<i32> | null (offset 84, 0 = null)
  lastRotationTrig: 'f32', // offset 88
  lastRotationSampleCounter: 'i32', // offset 92
  lastRotationTriggerSample: 'i32', // offset 96
  patternStartSample: 'i32', // offset 100
})

// Helper to read a StaticArray<i32> from a pointer
// StaticArray in AssemblyScript is a class with:
// - vtable pointer (4 bytes, offset 0)
// - length field (i32, 4 bytes, offset 4)
// - data array (starts at offset 8, capacity is fixed at allocation time)
// For noteStarts, shuffledIndices, rotatedIndices: capacity is MAX_NOTES (128)
export function readStaticInt32Array(
  buffer: ArrayBuffer | SharedArrayBuffer,
  ptr: number,
  maxLength: number = MAX_NOTES,
): Int32Array | null {
  if (ptr === 0) return null

  try {
    const view = new DataView(buffer, ptr)

    // Read length at offset 4 (after vtable pointer)
    const length = view.getInt32(4, true)

    // Validate length is reasonable
    if (length < 0 || length > maxLength * 2) {
      return null
    }

    // Data starts at offset 8 (after vtable + length)
    const actualLength = Math.min(length, maxLength)
    return new Int32Array(buffer, ptr + 8, actualLength)
  }
  catch {
    return null
  }
}
